'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/features/auth/hooks'
import { getJwtSubject } from '@/lib/auth/jwt-subject'
import {
  createWatchlist,
  deleteWatchlist,
  getWatchlists,
  renameWatchlist,
  reorderWatchlists,
  setWatchlist,
} from '@/service/watchlist.api'
import {
  CreateWatchlistRequest,
  DeleteWatchlistRequest,
  RenameWatchlistRequest,
  ReorderWatchlistsRequest,
  SetWatchlistRequest,
  Watchlist,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

export const watchlistKeys = {
  all: ['watchlists'] as const,
  lists: (userId: string) => [...watchlistKeys.all, 'list', userId] as const,
}

const getWatchlistUserKey = (userId?: string, accessToken?: string | null) =>
  userId || (accessToken ? getJwtSubject(accessToken) : null)

export const useGetWatchlists = () => {
  const { user, accessToken } = useAuth()
  const userKey = getWatchlistUserKey(user?.userId, accessToken)

  return useQuery<Watchlist[], ErrorResponse>({
    queryKey: watchlistKeys.lists(userKey ?? ''),
    queryFn: async () => {
      const res = await getWatchlists()
      return res.watchlists
    },
    // A successful token refresh can restore the access token before the client-side
    // auth profile has been rehydrated. The API is already usable at that point, so
    // gating on `user` alone left this query permanently idle and the widget stuck on
    // its skeleton. JWT `sub` keeps the cache user-scoped during that recovery window.
    enabled: Boolean(accessToken && userKey),
    // Watchlists only change through this client's own mutations, which invalidate the
    // key — a longer staleTime stops every dashboard page switch from refetching them.
    staleTime: 5 * 60 * 1000,
  })
}

export const useCreateWatchlist = (props?: {
  onSuccess?: (data: Watchlist, variables: CreateWatchlistRequest) => void
  onError?: (error: ErrorResponse, variables: CreateWatchlistRequest) => void
}) => {
  const queryClient = useQueryClient()
  const { user, accessToken } = useAuth()
  const userKey = getWatchlistUserKey(user?.userId, accessToken)
  return useMutation({
    mutationFn: (payload: CreateWatchlistRequest) => createWatchlist(payload),
    onSuccess: (data, variables) => {
      // Written synchronously into the cache (not just invalidated) so callers that
      // select the new watchlist immediately after creating it — see
      // WatchlistSelector's handleAddNew — don't hit the window before the
      // invalidation's refetch resolves, where the still-stale list doesn't contain
      // the new id yet and the active-watchlist fallback effect snaps selection back
      // to the first entry.
      queryClient.setQueryData<Watchlist[]>(watchlistKeys.lists(userKey ?? ''), (prev) =>
        prev ? [...prev, data] : [data]
      )
      queryClient.invalidateQueries({ queryKey: watchlistKeys.lists(userKey ?? '') })
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useSetWatchlist = (props?: {
  onSuccess?: (data: Watchlist, variables: SetWatchlistRequest) => void
  onError?: (error: ErrorResponse, variables: SetWatchlistRequest) => void
}) => {
  const queryClient = useQueryClient()
  const { user, accessToken } = useAuth()
  const userKey = getWatchlistUserKey(user?.userId, accessToken)
  return useMutation({
    mutationFn: (payload: SetWatchlistRequest) => setWatchlist(payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Watchlist[]>(
        watchlistKeys.lists(userKey ?? ''),
        (prev) => prev?.map((w) => (w.watchlistId === data.watchlistId ? data : w)) ?? [data]
      )
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useRenameWatchlist = (props?: {
  onSuccess?: (data: Watchlist, variables: RenameWatchlistRequest) => void
  onError?: (error: ErrorResponse, variables: RenameWatchlistRequest) => void
}) => {
  const queryClient = useQueryClient()
  const { user, accessToken } = useAuth()
  const userKey = getWatchlistUserKey(user?.userId, accessToken)
  return useMutation({
    mutationFn: (payload: RenameWatchlistRequest) => renameWatchlist(payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Watchlist[]>(
        watchlistKeys.lists(userKey ?? ''),
        (prev) => prev?.map((w) => (w.watchlistId === data.watchlistId ? data : w)) ?? [data]
      )
      props?.onSuccess?.(data, variables)
    },
    onError: props?.onError,
  })
}

export const useDeleteWatchlist = (props?: {
  onSuccess?: (variables: DeleteWatchlistRequest) => void
  onError?: (error: ErrorResponse, variables: DeleteWatchlistRequest) => void
}) => {
  const queryClient = useQueryClient()
  const { user, accessToken } = useAuth()
  const userKey = getWatchlistUserKey(user?.userId, accessToken)
  return useMutation({
    mutationFn: (payload: DeleteWatchlistRequest) => deleteWatchlist(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: watchlistKeys.lists(userKey ?? '') })
      props?.onSuccess?.(variables)
    },
    onError: props?.onError,
  })
}

export const useReorderWatchlists = (props?: {
  onSuccess?: (variables: ReorderWatchlistsRequest) => void
  onError?: (error: ErrorResponse, variables: ReorderWatchlistsRequest) => void
}) => {
  const queryClient = useQueryClient()
  const { user, accessToken } = useAuth()
  const userKey = getWatchlistUserKey(user?.userId, accessToken)
  return useMutation({
    mutationFn: (payload: ReorderWatchlistsRequest) => reorderWatchlists(payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Watchlist[]>(
        watchlistKeys.lists(userKey ?? ''),
        () => data.watchlists
      )
      props?.onSuccess?.(variables)
    },
    onError: props?.onError,
  })
}
